import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, CheckCircle2, XCircle, Eye, Clock, ChevronDown, ChevronUp } from "lucide-react";
import api, { fmtErr, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";

const FILTERS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]];
const DOC_LABELS = { gst_doc: "GST Certificate", photo_doc: "Photo / Selfie", pan_doc: "PAN / Driving License", aadhaar_doc: "Aadhaar / Passport / Voter ID" };

export default function AdminKyc() {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [open, setOpen] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(() => api.get(`/admin/kyc${filter ? `?status=${filter}` : ""}`).then(({ data }) => setList(data)).catch(() => {}), [filter]);
  useEffect(() => { load(); }, [load]);

  const viewDoc = async (fileId) => {
    try {
      const res = await api.get(`/kyc/docs/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const review = async (userId, action) => {
    if (action === "reject" && !reason.trim()) { toast.error("Rejection reason required"); return; }
    try {
      await api.post(`/admin/kyc/${userId}/review`, { action, reason: reason.trim() });
      toast.success(`KYC ${action}d`);
      setOpen(null);
      setReason("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const badge = (s) => s === "approved"
    ? <span className="flex w-fit items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-600"><CheckCircle2 className="h-3 w-3" /> APPROVED</span>
    : s === "rejected"
      ? <span className="flex w-fit items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-bold text-red-500"><XCircle className="h-3 w-3" /> REJECTED</span>
      : <span className="flex w-fit items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-600"><Clock className="h-3 w-3" /> PENDING</span>;

  return (
    <div data-testid="admin-kyc-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">KYC Review</h1>
          <p className="text-sm text-slate-500">Approve customers to unlock live booking.</p>
        </div>
        <ShieldCheck className="h-6 w-6 text-shipany-blue" />
      </div>

      <div className="mt-4 flex gap-2" data-testid="kyc-filters">
        {FILTERS.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`kyc-filter-${v || "all"}`}
            className={`rounded-lg px-4 py-2 text-xs font-bold ${filter === v ? "bg-shipany-blue text-white" : "bg-white text-slate-500 border border-slate-200"}`}>{label}</button>
        ))}
      </div>

      <div className="mt-4 space-y-3" data-testid="kyc-list">
        {(list || []).map((k) => (
          <div key={k.kyc_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`kyc-row-${k.email}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-shipany-navy">{k.customer_name || k.email} <span className="ml-1 text-xs font-normal text-slate-400">{k.company}</span></p>
                <p className="text-xs text-slate-400">{k.email} · submitted {fmtDateTime(k.submitted_at)}</p>
              </div>
              <div className="flex items-center gap-3">
                {badge(k.status)}
                <button onClick={() => { setOpen(open === k.user_id ? null : k.user_id); setReason(""); }} data-testid={`kyc-expand-${k.email}`}
                  className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                  {open === k.user_id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Details
                </button>
              </div>
            </div>

            {open === k.user_id && (
              <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`kyc-detail-${k.email}`}>
                <div className="grid gap-3 text-sm sm:grid-cols-3">
                  <div><p className="text-xs text-slate-400">PAN</p><p className="mono font-bold">{k.pan}</p></div>
                  <div><p className="text-xs text-slate-400">GSTIN</p><p className="mono font-bold">{k.gst}</p></div>
                  <div><p className="text-xs text-slate-400">Aadhaar</p><p className="mono font-bold">{k.aadhaar}</p></div>
                  <div><p className="text-xs text-slate-400">Bank Holder</p><p className="font-semibold">{k.bank?.holder}</p></div>
                  <div><p className="text-xs text-slate-400">Account</p><p className="mono font-bold">{k.bank?.account}</p></div>
                  <div><p className="text-xs text-slate-400">IFSC</p><p className="mono font-bold">{k.bank?.ifsc}</p></div>
                  <div className="sm:col-span-3"><p className="text-xs text-slate-400">Address</p><p className="font-semibold">{k.address?.line1}, {k.address?.city}, {k.address?.state} — {k.address?.pincode}</p></div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {Object.entries(DOC_LABELS).map(([key, label]) => k.documents?.[key] && (
                    <button key={key} onClick={() => viewDoc(k.documents[key].file_id)} data-testid={`kyc-doc-${key}-${k.email}`}
                      className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">
                      <Eye className="h-3 w-3" /> {label} <span className="text-slate-400">({k.documents[key].filename})</span>
                    </button>
                  ))}
                </div>
                {k.status === "pending" && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`kyc-reason-${k.email}`}
                      placeholder="Rejection reason (required to reject)" className="w-72 rounded-lg border border-slate-200 px-3 py-2 text-xs focus:border-shipany-blue focus:outline-none" />
                    <button onClick={() => review(k.user_id, "approve")} data-testid={`kyc-approve-${k.email}`}
                      className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approve & Activate
                    </button>
                    <button onClick={() => review(k.user_id, "reject")} data-testid={`kyc-reject-${k.email}`}
                      className="flex items-center gap-1.5 rounded-lg bg-red-500 px-4 py-2 text-xs font-bold text-white hover:bg-red-600">
                      <XCircle className="h-3.5 w-3.5" /> Reject
                    </button>
                  </div>
                )}
                {k.status !== "pending" && k.reason && <p className="mt-3 text-xs text-slate-500">Reason: {k.reason}</p>}
                {k.reviewed_by && <p className="mt-1 text-[11px] text-slate-400">Reviewed by {k.reviewed_by} · {fmtDateTime(k.reviewed_at)}</p>}
              </div>
            )}
          </div>
        ))}
        {list && list.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400" data-testid="kyc-empty">
            No {filter} KYC submissions.
          </div>
        )}
      </div>
    </div>
  );
}
